/**
 * Arrears snapshot schedule (METRIC-DEFINITIONS Q12/Q20 — Platform Admin,
 * Helix operators only).
 *
 * One master toggle per customer group: ON runs every member site at the
 * ONE group moment (group clock), so the group-level arrears roll-up is a
 * true point-in-time balance; OFF hands each site its own day/hour on its
 * own local clock. Whichever side is live is editable; the other side is
 * greyed, and every site row states what it will actually run at. Capture
 * itself is switched per site. Operator-set at onboarding, not tenant
 * self-service.
 */

import { useEffect, useState } from 'react';
import { platformSettingsAPI } from '../../../lib/api';
import type {
  ArrearsSnapshotGroupRow,
  ArrearsSnapshotSiteRow,
} from '../../../lib/api/platformSettings';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Switch } from '../../ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { CalendarClock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

function clampedInt(value: string, min: number, max: number): number | null {
  const n = Number.parseInt(value, 10);
  if (Number.isNaN(n) || n < min || n > max) return null;
  return n;
}

function ordinal(day: number): string {
  const rem10 = day % 10;
  const rem100 = day % 100;
  if (rem10 === 1 && rem100 !== 11) return `${day}st`;
  if (rem10 === 2 && rem100 !== 12) return `${day}nd`;
  if (rem10 === 3 && rem100 !== 13) return `${day}rd`;
  return `${day}th`;
}

interface DayHourInputsProps {
  day: number;
  hour: number;
  disabled: boolean;
  onSave: (patch: { arrears_snapshot_day?: number; arrears_snapshot_hour?: number }) => void;
}

function DayHourInputs({ day, hour, disabled, onSave }: DayHourInputsProps) {
  // Keyed on value + disabled: these are uncontrolled inputs (defaultValue),
  // so a remount is what re-syncs them when a save lands or the master
  // toggle flips — otherwise typed-but-unsaved text survives the greying.
  return (
    <div className={`flex items-center gap-2 ${disabled ? 'opacity-50' : ''}`}>
      <Input
        key={`d-${day}-${disabled}`}
        type="number" min={1} max={28} className="w-16" defaultValue={day}
        disabled={disabled}
        onBlur={(e) => {
          const v = clampedInt(e.target.value, 1, 28);
          if (v === null) {
            toast.error('Day must be between 1 and 28');
            e.target.value = String(day);
            return;
          }
          if (v !== day) onSave({ arrears_snapshot_day: v });
        }}
      />
      <span className="text-xs text-muted-foreground">at</span>
      <Input
        key={`h-${hour}-${disabled}`}
        type="number" min={0} max={23} className="w-16" defaultValue={hour}
        disabled={disabled}
        onBlur={(e) => {
          const v = clampedInt(e.target.value, 0, 23);
          if (v === null) {
            toast.error('Hour must be between 0 and 23');
            e.target.value = String(hour);
            return;
          }
          if (v !== hour) onSave({ arrears_snapshot_hour: v });
        }}
      />
      <span className="text-xs text-muted-foreground">:00</span>
    </div>
  );
}

export function ArrearsSnapshotScheduleSection() {
  const [groups, setGroups] = useState<ArrearsSnapshotGroupRow[]>([]);
  const [sites, setSites] = useState<ArrearsSnapshotSiteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await platformSettingsAPI.getArrearsSnapshotConfig();
        setGroups(data.groups);
        setSites(data.sites);
      } catch (err: unknown) {
        console.error('Error loading arrears snapshot config:', err);
        toast.error('Failed to load the arrears snapshot schedules');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const saveGroup = async (
    group: ArrearsSnapshotGroupRow,
    patch: Partial<ArrearsSnapshotGroupRow>,
  ) => {
    setSaving(true);
    try {
      await platformSettingsAPI.updateArrearsSnapshotGroup({
        group_id: group.group_id, ...patch,
      });
      setGroups((current) => current.map((g) =>
        g.group_id === group.group_id ? { ...g, ...patch } : g));
      toast.success(`Saved ${group.group_name} schedule`);
    } catch (err: unknown) {
      console.error('Error saving group schedule:', err);
      toast.error(`Failed to save ${group.group_name}`);
    } finally {
      setSaving(false);
    }
  };

  const saveSite = async (
    site: ArrearsSnapshotSiteRow,
    patch: Partial<ArrearsSnapshotSiteRow>,
  ) => {
    setSaving(true);
    try {
      const { data } = await platformSettingsAPI.updateArrearsSnapshotSite({
        company_id: site.company_id, ...patch,
      });
      setSites((current) => current.map((s) =>
        s.company_id === site.company_id ? data : s));
      toast.success(`Saved ${site.company_name}`);
    } catch (err: unknown) {
      console.error('Error saving site schedule:', err);
      toast.error(`Failed to save ${site.company_name}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const ungrouped = sites.filter((s) => s.group_id === null);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarClock className="h-5 w-5" />
          Arrears Snapshot Schedule
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          When the previous month&apos;s arrears balance is frozen. With the group
          schedule on, every site in the group freezes at the same instant on the
          group&apos;s clock, so the group roll-up is a single point-in-time balance.
          The live setting is editable; the inactive side is greyed out.
        </p>
      </CardHeader>
      <CardContent className="space-y-8">
        {groups.map((group) => {
          const members = sites.filter((s) => s.group_id === group.group_id);
          const grouped = group.arrears_snapshot_group_schedule;
          const groupLabel =
            `${ordinal(group.arrears_snapshot_day)}, ` +
            `${String(group.arrears_snapshot_hour).padStart(2, '0')}:00 ` +
            `(${group.timezone})`;
          return (
            <div key={group.group_id}>
              <div className="flex items-center justify-between rounded-md border border-input bg-muted/40 px-4 py-3 mb-2">
                <div>
                  <p className="text-sm font-semibold">{group.group_name}</p>
                  <p className="text-xs text-muted-foreground">
                    Run all sites on one group schedule — every site freezes
                    together in the group timezone ({group.timezone})
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <Switch
                    checked={grouped}
                    disabled={saving}
                    onCheckedChange={(checked) =>
                      saveGroup(group, { arrears_snapshot_group_schedule: checked })}
                  />
                  <DayHourInputs
                    day={group.arrears_snapshot_day}
                    hour={group.arrears_snapshot_hour}
                    disabled={saving || !grouped}
                    onSave={(patch) => saveGroup(group, patch)}
                  />
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Site</TableHead>
                    <TableHead>Capture</TableHead>
                    <TableHead>Site schedule (site-local)</TableHead>
                    <TableHead>Runs at</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map((site) => (
                    <TableRow key={site.company_id}>
                      <TableCell className="font-medium">{site.company_name}</TableCell>
                      <TableCell>
                        <Switch
                          checked={site.capture_arrears_snapshots}
                          disabled={saving}
                          onCheckedChange={(checked) =>
                            saveSite(site, { capture_arrears_snapshots: checked })}
                        />
                      </TableCell>
                      <TableCell>
                        <DayHourInputs
                          day={site.arrears_snapshot_day}
                          hour={site.arrears_snapshot_hour}
                          disabled={saving || grouped}
                          onSave={(patch) => saveSite(site, patch)}
                        />
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {!site.capture_arrears_snapshots
                          ? 'capture off'
                          : grouped
                            ? `with group: ${groupLabel}`
                            : `${ordinal(site.arrears_snapshot_day)}, ` +
                              `${String(site.arrears_snapshot_hour).padStart(2, '0')}:00 ` +
                              'site-local'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          );
        })}
        {ungrouped.length > 0 && (
          <div>
            <p className="text-sm font-semibold mb-2">Sites with no group</p>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Site</TableHead>
                  <TableHead>Capture</TableHead>
                  <TableHead>Schedule (site-local)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ungrouped.map((site) => (
                  <TableRow key={site.company_id}>
                    <TableCell className="font-medium">{site.company_name}</TableCell>
                    <TableCell>
                      <Switch
                        checked={site.capture_arrears_snapshots}
                        disabled={saving}
                        onCheckedChange={(checked) =>
                          saveSite(site, { capture_arrears_snapshots: checked })}
                      />
                    </TableCell>
                    <TableCell>
                      <DayHourInputs
                        day={site.arrears_snapshot_day}
                        hour={site.arrears_snapshot_hour}
                        disabled={saving}
                        onSave={(patch) => saveSite(site, patch)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {groups.length === 0 && ungrouped.length === 0 && (
          <p className="text-sm text-muted-foreground py-4">No customer sites found.</p>
        )}
      </CardContent>
    </Card>
  );
}
